import {useState, useRef, useMemo, useEffect} from "react";
import {ApolloClient, ApolloLink, ApolloQueryResult} from "@apollo/client";
import {
    MeQueryQuery,
    MeQueryDocument,
    MeField
} from "ubcommunity-shared/src/types";
import {setContext} from "@apollo/client/link/context";
import useAuth from "ubcommunity-shared/src/Auth/hooks/useAuth";
import {hasAuthorization, getAuthorizationHeaderFor} from "ubcommunity-shared/src/Auth/utils";
import {cleanTypeName, errorMiddleware, httpLink} from "ubcommunity-shared/src/ApiProvider/utils";
import {apolloCache} from "ubcommunity-shared/src/ApiProvider/cache";

const getMeFromMeQueryResult = ({ data: {me} = {me: null} }: ApolloQueryResult<MeQueryQuery>): MeField | null => me

export default function useMeQuery (accessToken: {scheme: string, token: string} | null): {me: MeField | null, error: string | null} {
    const isAuthorized = hasAuthorization(undefined, accessToken);
    const [me, setMe] = useState(null);
    const [error, setError] = useState(null);
    const [apolloQuery, setApolloQuery] = useState(null);
    const {authState, setAuth} = useAuth();
    const queryRequest = useRef(null);

    /**
     *  This duplicates code in `useApiClient`
     *
     *  `useMeQuery` requires an authorization header to be sent with the query request.
     *  The state used to generate that header is available to `useApiClient` but the Apollo
     *  client instance in that hook is not updating its headers by calling `setContext`
     *
     *  I suspect that there's only a teency weency change that needs to be made
     *  somewhere 🙃 (and probably by correctly implementing `useMeQueryQuery` from
     *  "ubcommunity-shared/src/types"!)
     *
     *  In the meantime, please excuse this expedient wiffle
     */
    const client = useMemo(() => {
        const authLink = setContext((_, {headers}) => ({
            headers: {
                ...headers,
                authorization: getAuthorizationHeaderFor(undefined, accessToken)
            }
        }));

        return new ApolloClient({
            link: ApolloLink.from([
                cleanTypeName,
                errorMiddleware(setAuth),
                authLink,
                httpLink,
            ]),
            cache: apolloCache,
        });
    }, [accessToken, setAuth])

    useEffect(() => {
        if (isAuthorized && !me) {
            if (queryRequest.current) return
            queryRequest.current = (
                client.query({ query: MeQueryDocument })
                    .then((meQueryResult: ApolloQueryResult<MeQueryQuery>) => {
                        setMe(getMeFromMeQueryResult(meQueryResult));
                        setError(null)
                    })
                    .catch(({ message }) => {
                        setMe(null)
                        setError(message);
                    })
                    .then(() => {
                        setApolloQuery(null)
                    })
            )
        }
    }, [isAuthorized, me, apolloQuery, client])

    useEffect(() => {
        if (me) {
            if (authState?.me !== me) {
                setAuth({ ...authState ?? {}, me })
            }
        }
    }, [me, authState, setAuth])

    return {me, error}
}
